import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { container } from '@infrastructure/container';
import { Note } from '@domain/purchasing/models/Notes';
import { GetNotesFilters } from '@domain/purchasing/INotesRepository';

export const useNotes = (filters?: GetNotesFilters) => {
  const queryClient = useQueryClient();

  const { data: notes, isLoading, error } = useQuery({
    queryKey: ['notes', filters],
    queryFn: async () => {
      const result = await container.notesRepository.getNotes(filters);
      if (result.status === 'error') {
        throw new Error(result.error.message);
      }
      return result.data;
    },
    staleTime: 0,
    refetchOnWindowFocus: false,
  });

  const createNoteMutation = useMutation({
    mutationFn: async ({ 
      note, 
    }: { 
      note: Omit<Note, 'id' | 'createdAt' | 'updatedAt'>;
    }) => {
      const result = await container.notesRepository.createNote(note);
      if (result.status === 'error') {
        throw new Error(result.error.message);
      }
      return result.data;
    },
    onMutate: async ({ note }) => {
      // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({ queryKey: ['notes', filters] });

      // Snapshot the previous value
      const previousNotes = queryClient.getQueryData<Note[]>(['notes', filters]);

      // Optimistically update to the new value
      const optimisticNote: Note = {
        ...note,
        id: `temp-${Date.now()}`, // Temporary ID
        createdAt: new Date(),
        updatedAt: new Date(),
      };

      queryClient.setQueryData<Note[]>(['notes', filters], (old) => [
        ...(old || []),
        optimisticNote,
      ]);

      // Return a context object with the snapshotted value
      return { previousNotes };
    },
    onError: (_, __, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousNotes) {
        queryClient.setQueryData(['notes', filters], context.previousNotes);
      }
    },
    onSuccess: (createdNote) => {
      // Replace the optimistic note with the real one from the server
      queryClient.setQueryData<Note[]>(['notes', filters], (old) => 
        (old || []).map(note => 
          note.id && note.id.startsWith('temp-') ? createdNote : note
        )
      );
    },
    onSettled: () => {
      // Always refetch after error or success to ensure we have the correct data
      queryClient.invalidateQueries({ queryKey: ['notes'] });
      // Also invalidate purchase order queries since notes are related to purchase orders
      if (filters?.purchaseOrderId) {
        queryClient.invalidateQueries({ queryKey: ['purchaseOrder', filters.purchaseOrderId] });
      }
    },
  });

  const updateNoteMutation = useMutation({
    mutationFn: async ({ id, note }: { id: string; note: Omit<Note, 'id' | 'createdAt' | 'updatedAt'> }) => {
      const result = await container.notesRepository.updateNote(id, note);
      if (result.status === 'error') {
        throw new Error(result.error.message);
      }
      return result.data;
    },
    onMutate: async ({ id, note }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['notes', filters] });

      // Snapshot the previous value
      const previousNotes = queryClient.getQueryData<Note[]>(['notes', filters]);

      // Optimistically update the note
      queryClient.setQueryData<Note[]>(['notes', filters], (old) =>
        (old || []).map(existingNote =>
          existingNote.id === id
            ? { 
                ...existingNote, 
                ...note, 
                id: existingNote.id,
                createdAt: existingNote.createdAt,
                updatedAt: new Date()
              }
            : existingNote
        )
      );

      return { previousNotes };
    },
    onError: (_, __, context) => {
      // Roll back on error
      if (context?.previousNotes) {
        queryClient.setQueryData(['notes', filters], context.previousNotes);
      }
    },
    onSuccess: (updatedNote) => {
      // Update with the real data from server
      queryClient.setQueryData<Note[]>(['notes', filters], (old) =>
        (old || []).map(note =>
          note.id === updatedNote.id ? updatedNote : note
        )
      );
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['notes'] });
      // Also invalidate purchase order queries since notes are related to purchase orders
      if (filters?.purchaseOrderId) {
        queryClient.invalidateQueries({ queryKey: ['purchaseOrder', filters.purchaseOrderId] });
      }
    },
  });

  const deleteNoteMutation = useMutation({
    mutationFn: async (id: string) => {
      const result = await container.notesRepository.deleteNote(id);
      if (result.status === 'error') {
        throw new Error(result.error.message);
      }
      return result.data;
    },
    onMutate: async (id) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['notes', filters] });

      // Snapshot the previous value
      const previousNotes = queryClient.getQueryData<Note[]>(['notes', filters]);

      // Optimistically remove the note
      queryClient.setQueryData<Note[]>(['notes', filters], (old) =>
        (old || []).filter(note => note.id !== id)
      );

      return { previousNotes };
    },
    onError: (_, __, context) => {
      // Roll back on error
      if (context?.previousNotes) {
        queryClient.setQueryData(['notes', filters], context.previousNotes);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['notes'] });
      // Also invalidate purchase order queries since notes are related to purchase orders
      if (filters?.purchaseOrderId) {
        queryClient.invalidateQueries({ queryKey: ['purchaseOrder', filters.purchaseOrderId] });
      }
    },
  });

  const createNote = async (
    note: Omit<Note, 'id' | 'createdAt' | 'updatedAt'>,
  ) => {
    return createNoteMutation.mutateAsync({ note });
  };

  const updateNote = async (id: string, note: Omit<Note, 'id' | 'createdAt' | 'updatedAt'>) => {
    return updateNoteMutation.mutateAsync({ id, note });
  };

  const deleteNote = async (id: string) => {
    return deleteNoteMutation.mutateAsync(id);
  };

  return {
    notes: notes || [],
    isLoading,
    error,
    createNote,
    updateNote,
    deleteNote,
    isCreating: createNoteMutation.isPending,
    isUpdating: updateNoteMutation.isPending,
    isDeleting: deleteNoteMutation.isPending,
  };
};
